import { trpc } from './trpc'
import { inferRouterOutputs, inferRouterInputs } from '@trpc/server'
import { AppRouter } from '@tagadapay/trpc-types'
import { useToast } from '@/components/ui/use-toast'

type OutputRouter = inferRouterOutputs<AppRouter>
type InputRouter = inferRouterInputs<AppRouter>

// Output types
export type PaymentFlowsList = OutputRouter['paymentFlows']['list']
export type PaymentFlowItem = PaymentFlowsList[number]
export type PaymentFlowsListWithProcessors =
    OutputRouter['paymentFlows']['listWithProcessors']
export type PaymentFlowItemWithProcessors = PaymentFlowsListWithProcessors['flows'][number]
export type PaymentFlowDetails = OutputRouter['paymentFlows']['getById']
export type PaymentFlowDetailsWithProcessors =
    OutputRouter['paymentFlows']['getByIdWithProcessors']
export type CreatePaymentFlowOutput = OutputRouter['paymentFlows']['create']
export type UpdatePaymentFlowOutput = OutputRouter['paymentFlows']['update']
export type DeletePaymentFlowOutput = OutputRouter['paymentFlows']['delete']
export type PaymentFlowFeatures = OutputRouter['paymentFlows']['getFeatures']
export type DuplicatePaymentFlowOutput = OutputRouter['paymentFlows']['duplicate']

// Input types
export type ListPaymentFlowsInput = InputRouter['paymentFlows']['list']
export type GetPaymentFlowInput = InputRouter['paymentFlows']['getById']
export type CreatePaymentFlowInput = InputRouter['paymentFlows']['create']
export type UpdatePaymentFlowInput = InputRouter['paymentFlows']['update']
export type DeletePaymentFlowInput = InputRouter['paymentFlows']['delete']
export type DuplicatePaymentFlowInput = InputRouter['paymentFlows']['duplicate']

// Query hooks
export const usePaymentFlowsList = (enabled = true) => {
    return trpc.paymentFlows.list.useQuery(undefined, { enabled })
}

export const usePaymentFlowsListWithProcessors = (enabled = true) => {
    return trpc.paymentFlows.listWithProcessors.useQuery({}, { enabled, staleTime: 15_000, refetchOnWindowFocus: true })
}

export const usePaymentFlow = (flowId: string, enabled = true) => {
    return trpc.paymentFlows.getById.useQuery(
        { flowId },
        {
            enabled: enabled && !!flowId,
            retry: 2,
            staleTime: 30000,
        },
    )
}

export const usePaymentFlowWithProcessors = (flowId: string, enabled = true) => {
    return trpc.paymentFlows.getByIdWithProcessors.useQuery(
        { flowId },
        {
            enabled: enabled && !!flowId,
            retry: 2,
            staleTime: 15_000,
            refetchOnWindowFocus: true,
        },
    )
}

export const usePaymentFlowFeatures = () => {
    return trpc.paymentFlows.getFeatures.useQuery(undefined, {
        staleTime: 60_000,
    })
}

// Mutation hooks
export const useCreatePaymentFlow = (options?: { silent?: boolean }) => {
    const { toast } = useToast()
    const utils = trpc.useUtils()

    return trpc.paymentFlows.create.useMutation({
        onSuccess: () => {
            utils.paymentFlows.list.invalidate()
            utils.paymentFlows.listWithProcessors.invalidate()
            if (options?.silent) return
            toast({
                title: 'Success',
                description: 'Payment flow created successfully',
            })
        },
        onError: (error) => {
            if (options?.silent) return
            toast({
                title: 'Error',
                description: error.message || 'Failed to create payment flow',
                variant: 'destructive',
            })
        },
    })
}

export const useUpdatePaymentFlow = (options?: { silent?: boolean }) => {
    const { toast } = useToast()
    const utils = trpc.useUtils()

    return trpc.paymentFlows.update.useMutation({
        onSuccess: () => {
            utils.paymentFlows.list.invalidate()
            utils.paymentFlows.listWithProcessors.invalidate()
            utils.paymentFlows.getById.invalidate()
            utils.paymentFlows.getByIdWithProcessors.invalidate()
            if (options?.silent) return
            toast({
                title: 'Success',
                description: 'Payment flow updated successfully',
            })
        },
        onError: (error) => {
            if (options?.silent) return
            toast({
                title: 'Error',
                description: error.message || 'Failed to update payment flow',
                variant: 'destructive',
            })
        },
    })
}

export const useDuplicatePaymentFlow = () => {
    const { toast } = useToast()
    const utils = trpc.useUtils()

    return trpc.paymentFlows.duplicate.useMutation({
        onSuccess: () => {
            utils.paymentFlows.list.invalidate()
            utils.paymentFlows.listWithProcessors.invalidate()
            toast({
                title: 'Success',
                description: 'Payment flow duplicated successfully',
            })
        },
        onError: (error) => {
            toast({
                title: 'Error',
                description: error.message || 'Failed to duplicate payment flow',
                variant: 'destructive',
            })
        },
    })
}

export const useDeletePaymentFlow = () => {
    const { toast } = useToast()
    const utils = trpc.useUtils()

    return trpc.paymentFlows.delete.useMutation({
        onSuccess: () => {
            utils.paymentFlows.list.invalidate()
            utils.paymentFlows.listWithProcessors.invalidate()
            toast({
                title: 'Success',
                description: 'Payment flow deleted successfully',
            })
        },
        onError: (error) => {
            toast({
                title: 'Error',
                description: error.message || 'Failed to delete payment flow',
                variant: 'destructive',
            })
        },
    })
}

